/**
 * Functional wrapper around the class-based ExportPdfModal.
 *
 * Provides `getPdfPreviewLink`, `exportFolderPdf`, and preview-state
 * management via TanStack Query hooks so the class component stays
 * hook-free. Replaces the compose-app `getPdfPreviewLink` / `exportFolderPdf`
 * props previously dispatched from StoryList.
 */
import React, { useState } from 'react';

import { IExportPdfRequest, IPreviewPdfFolderRequest } from '../../../../types';
import { useExportFolderPdf, usePreviewFolderPdf } from '../hooks';
import { ExportPdfModal, IExportPdfModalProps } from './ExportPdfModal';

export interface IExportPdfModalContainerProps
  extends Omit<
    IExportPdfModalProps,
    'getPdfPreviewLink' | 'isPreviewingPdf' | 'isPreviewingPdfFailure' | 'previewFolderPdfError' | 'exportFolderPdf'
  > {
  entityId: number;
  entitySubdomain: string;
  /** Error messages for PDF hooks — sourced from IStoryListMessages, not IModalMessages */
  pdfGenerateFailed?: string;
  pdfGenerateFailedDescrption?: string;
  exportFolderPdfFailed?: string;
}

export const ExportPdfModalContainer: React.FC<IExportPdfModalContainerProps> = ({
  entityId,
  entitySubdomain,
  messages,
  pdfGenerateFailed = '',
  pdfGenerateFailedDescrption = '',
  exportFolderPdfFailed = '',
  ...rest
}) => {
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false);
  const [isPreviewingPdfFailure, setIsPreviewingPdfFailure] = useState(false);
  const [previewFolderPdfError, setPreviewFolderPdfError] = useState('');

  const previewMutation = usePreviewFolderPdf({
    title: pdfGenerateFailed,
    body: pdfGenerateFailedDescrption
  });

  const exportMutation = useExportFolderPdf({
    title: exportFolderPdfFailed
  });

  const getPdfPreviewLink = async (previewProps: IPreviewPdfFolderRequest): Promise<string> => {
    setIsPreviewingPdf(true);
    setIsPreviewingPdfFailure(false);
    setPreviewFolderPdfError('');
    try {
      const result = await previewMutation.mutateAsync({
        ...previewProps,
        entityId,
        entitySubdomain
      });
      setIsPreviewingPdf(false);
      return result;
    } catch (error: any) {
      setIsPreviewingPdf(false);
      if (error?.name !== 'AbortError') {
        setIsPreviewingPdfFailure(true);
        setPreviewFolderPdfError(
          JSON.stringify({ msg: error?.message ?? 'UNKNOWN_ERROR' })
        );
      }
      return '';
    }
  };

  const exportFolderPdf = (exportProps: IExportPdfRequest): Promise<unknown> =>
    exportMutation.mutateAsync({ ...exportProps, entityId });

  return (
    <ExportPdfModal
      {...rest}
      messages={messages}
      getPdfPreviewLink={getPdfPreviewLink}
      isPreviewingPdf={isPreviewingPdf}
      isPreviewingPdfFailure={isPreviewingPdfFailure}
      previewFolderPdfError={previewFolderPdfError}
      exportFolderPdf={exportFolderPdf}
    />
  );
};
